/**
 * useDeveloperRegistrationForm Hook
 * Orchestrates access code validation and form submission using CQRS mediator pattern
 */

import { useCallback, useRef } from 'react';
import { logger } from '@/application/core';
import { mediator } from '@/application';
import {
  ValidateAccessCodeQuery,
  PROGRAM_TYPE,
  type ProgramType,
  type SubmitDeveloperRegistrationResponse,
  type ValidateAccessCodeResponse,
} from '@/application/queries/developerRegistration';
import { SubmitDeveloperRegistrationCommand } from '@/application/commands/developerRegistration';
import {
  useDeveloperRegistrationFormStore,
  type AccessCodeValidationResult,
} from './DeveloperRegistrationForm.store';

/**
 * Form data interface matching the backend DTO
 */
export interface DeveloperRegistrationFormData {
  companyName: string;
  companyAddress: string;
  ownerContactName: string;
  ownerContactEmail: string;
  technicalContactName?: string;
  technicalContactEmail?: string;
  accessCode?: string;
  programType?: ProgramType;
}

/**
 * Props for the hook
 */
export interface UseDeveloperRegistrationFormProps {
  antiforgeryToken: string;
  programType?: ProgramType;
}

/**
 * Hook return type
 */
export interface UseDeveloperRegistrationFormResult {
  // State
  accessCodeValidating: boolean;
  accessCodeResult: AccessCodeValidationResult | null;
  accessCodeError: string | null;
  isSubmitting: boolean;
  submitError: string | null;
  fieldErrors: Record<string, string>;

  // Actions
  validateAccessCode: (code: string) => Promise<void>;
  submitForm: (data: DeveloperRegistrationFormData) => Promise<void>;
  clearFieldError: (field: string) => void;
  setSubmitError: (error: string | null) => void;
  reset: () => void;
}

/**
 * Hook to manage developer registration form logic
 * Handles access code validation with debouncing and form submission
 */
export function useDeveloperRegistrationForm(
  props: UseDeveloperRegistrationFormProps
): UseDeveloperRegistrationFormResult {
  const { antiforgeryToken, programType } = props;

  // Get state values using individual selectors (ensures proper reactivity without infinite loops)
  const accessCodeValidating = useDeveloperRegistrationFormStore(
    (state) => state.accessCodeValidating
  );
  const accessCodeResult = useDeveloperRegistrationFormStore((state) => state.accessCodeResult);
  const accessCodeError = useDeveloperRegistrationFormStore((state) => state.accessCodeError);
  const isSubmitting = useDeveloperRegistrationFormStore((state) => state.isSubmitting);
  const submitError = useDeveloperRegistrationFormStore((state) => state.submitError);
  const fieldErrors = useDeveloperRegistrationFormStore((state) => state.fieldErrors);

  // Get actions using individual selectors (stable references, don't cause re-renders)
  const setAccessCodeValidating = useDeveloperRegistrationFormStore(
    (state) => state.setAccessCodeValidating
  );
  const setAccessCodeResult = useDeveloperRegistrationFormStore(
    (state) => state.setAccessCodeResult
  );
  const setAccessCodeError = useDeveloperRegistrationFormStore((state) => state.setAccessCodeError);
  const setSubmitting = useDeveloperRegistrationFormStore((state) => state.setSubmitting);
  const setSubmitError = useDeveloperRegistrationFormStore((state) => state.setSubmitError);
  const setFieldErrors = useDeveloperRegistrationFormStore((state) => state.setFieldErrors);
  const clearFieldError = useDeveloperRegistrationFormStore((state) => state.clearFieldError);
  const reset = useDeveloperRegistrationFormStore((state) => state.reset);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Validate access code with debouncing (300ms)
   * Uses CQRS mediator pattern instead of direct fetch
   */
  const validateAccessCode = useCallback(
    async (code: string) => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // If empty, clear validation state
      if (!code || code.trim() === '') {
        setAccessCodeResult(null);
        return;
      }

      // Debounce the validation call
      debounceTimerRef.current = setTimeout(async () => {
        setAccessCodeValidating(true);

        try {
          // Use mediator to send query
          const result = await mediator.send<ValidateAccessCodeResponse>(
            new ValidateAccessCodeQuery(code, antiforgeryToken)
          );

          // Transform response to store format
          const validationResult: AccessCodeValidationResult = {
            isValid: result.isValid,
            accessCodeType: result.accessCodeType,
            salespersonEmail: result.salespersonEmail,
            errorMessage: result.errorMessage || null,
          };

          setAccessCodeResult(validationResult);

          if (!result.isValid && result.errorMessage) {
            setAccessCodeError(result.errorMessage);
          }
        } catch (err) {
          logger.error('validateAccessCode', 'Error:', err);
          setAccessCodeError('Failed to validate access code');
        }
      }, 300);
    },
    [antiforgeryToken, setAccessCodeValidating, setAccessCodeResult, setAccessCodeError]
  );

  /**
   * Submit the registration form
   * Uses CQRS mediator pattern instead of direct fetch
   */
  const submitForm = useCallback(
    async (data: DeveloperRegistrationFormData) => {
      setSubmitting(true);
      setSubmitError(null);
      setFieldErrors({});

      try {
        // Use mediator to send query
        const result = await mediator.send<SubmitDeveloperRegistrationResponse>(
          new SubmitDeveloperRegistrationCommand(
            { ...data, programType: programType || PROGRAM_TYPE.DEVELOPER },
            antiforgeryToken
          )
        );

        if (!result.success) {
          // Handle validation errors
          if (result.validationErrors) {
            const errors: Record<string, string> = {};
            for (const [field, messages] of Object.entries(result.validationErrors)) {
              // Convert PascalCase to camelCase for form field names
              const camelField = field.charAt(0).toLowerCase() + field.slice(1);
              errors[camelField] = messages[0] || 'Invalid value';
            }
            setFieldErrors(errors);
          }

          setSubmitError(result.errorMessage || 'Form submission failed');
          setSubmitting(false); // Reset loading state on validation failure
          return;
        }

        // Success - redirect to appropriate URL
        // Demo program uses redirectUrl, Developer program uses stripeUrl
        const redirectTarget = result.redirectUrl || result.stripeUrl;
        if (redirectTarget) {
          window.location.href = redirectTarget;
          // NOTE: Keep isSubmitting = true during redirect to maintain loading state
        } else {
          setSubmitError('No redirect URL received. Please try again.');
          setSubmitting(false);
        }
      } catch {
        setSubmitError('An unexpected error occurred. Please try again.');
        setSubmitting(false);
      }
    },
    [antiforgeryToken, programType, setSubmitting, setSubmitError, setFieldErrors]
  );

  return {
    // State
    accessCodeValidating,
    accessCodeResult,
    accessCodeError,
    isSubmitting,
    submitError,
    fieldErrors,

    // Actions
    validateAccessCode,
    submitForm,
    clearFieldError,
    setSubmitError,
    reset,
  };
}
